import { CARD_COUNT, seededPicker, type TarotField, type TarotTextTable } from './tarot'
import type { Direction } from './tarotTopic'

/**
 * 질문 타로 — 카드 **3장**.
 *
 * 뽑는 방식·카드 그림·뜻은 켈틱 크로스와 **같은 것**을 쓴다 (`src/lib/tarot.ts` 한 벌).
 * 다른 것은 자리 수와 자리의 뜻뿐이다.
 *
 * **자리는 두 벌이다.** 질문의 결이 다르기 때문이다.
 *
 *   decide  now / block / try    지금 상황 · 흐름을 막는 것 · 해 볼 만한 것
 *           → **결정과 행동**을 묻는 질문 (이직해도 될까, 먼저 연락해도 될까)
 *   state   now / sign / move    지금 자리 · 바뀌는 신호 · 내가 할 것
 *           → **상태와 까닭**을 묻는 질문 (번아웃일까, 왜 이렇게 많이 썼을까)
 *
 * 한 벌로만 두었더니 "이거 번아웃일까" 에도 "흐름을 막는 것" 이 나와 결이 어긋났다.
 *
 * 열 장짜리와 나눠 둔 까닭: 열 장은 질문을 적어 넣는 **유료 상품**이고, 이쪽은
 * 정해진 질문 하나에 짧게 답하는 입구다. 자리 수가 달라 저장 모양도 다르다.
 */

/** 결정·행동을 묻는 질문의 자리 */
export const ASK_POSITIONS_DECIDE = ['now', 'block', 'try'] as const
/** 상태·까닭을 묻는 질문의 자리 */
export const ASK_POSITIONS_STATE = ['now', 'sign', 'move'] as const

export type AskShape = 'decide' | 'state'
export type AskPosition =
  | (typeof ASK_POSITIONS_DECIDE)[number]
  | (typeof ASK_POSITIONS_STATE)[number]

export const ASK_SHAPES: Record<AskShape, readonly AskPosition[]> = {
  decide: ASK_POSITIONS_DECIDE,
  state: ASK_POSITIONS_STATE,
}

export const ASK_SIZE = 3

export function positionsOf(shape: AskShape = 'decide'): readonly AskPosition[] {
  return ASK_SHAPES[shape]
}

export interface AskCard {
  position: AskPosition
  id: number
  direction: Direction
}

/**
 * 석 장을 펼친다. **같은 카드가 두 번 나오지 않는다.**
 * 씨앗을 주면 같은 패가 나온다 (저장된 판을 다시 열 때).
 */
export function drawAsk(seed?: string, shape: AskShape = 'decide'): AskCard[] {
  const pick = seededPicker(seed ?? `${Date.now()}|${Math.random()}`)
  const used = new Set<number>()
  const out: AskCard[] = []
  for (const position of positionsOf(shape)) {
    let id = pick(CARD_COUNT)
    for (let guard = 0; used.has(id) && guard < CARD_COUNT * 4; guard += 1) id = pick(CARD_COUNT)
    used.add(id)
    out.push({ position, id, direction: pick(2) === 0 ? 'upright' : 'reversed' })
  }
  return out
}

export interface AskReading {
  position: AskPosition
  cardId: number
  cardName: string
  direction: Direction
  /** 카드의 한 줄 뜻 */
  summary: string
  /** 그 주제에 맞춘 본문 */
  body: string
}

/** 자리 × 카드 를 합쳐 석 자리 재료를 만든다. **AI 없이** 자료만 쓴다. */
export function readAsk(cards: AskCard[], table: TarotTextTable, field: TarotField): AskReading[] {
  return cards.map((card) => {
    const entry = table[String(card.id)]
    if (!entry) throw new Error(`카드 문장이 없다: ${card.id}`)
    const side = entry[card.direction]
    return {
      position: card.position,
      cardId: card.id,
      cardName: entry.name,
      direction: card.direction,
      summary: side.summary,
      body: side[field] || side.summary,
    }
  })
}

